import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Kicker } from "@/components/ui/kicker";
import { Tonal } from "@/components/ui/tonal";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { FindingRow } from "@/components/experience/finding-row";
import type { FindingType, Severity } from "@/components/finding-badges";
import {
  FindingsFilter,
  findingMatchesFilter,
  type FindingsFilterStatus,
  type FindingsFilterSeverity,
} from "@/components/experience/findings-filter";

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 3,
  major: 2,
  minor: 1,
  nit: 0,
};

export interface PersonaFindingItem {
  id: string;
  title?: string | null;
  description: string;
  type: string;
  severity: string | null;
  status?: string | null;
  state?: string | null;
  triageStatus?: string | null;
  createdAt?: string | null;
}

interface PersonaFindingsSectionProps {
  findings: PersonaFindingItem[];
  projectId: string;
  personaId: string;
  firstName: string;
}

export function PersonaFindingsSection({
  findings,
  projectId,
  personaId,
  firstName,
}: PersonaFindingsSectionProps) {
  const [findingStatus, setFindingStatus] =
    useState<FindingsFilterStatus>("open");
  const [findingSeverity, setFindingSeverity] =
    useState<FindingsFilterSeverity>("all");

  const filtered = useMemo(() => {
    return findings.filter((f) =>
      findingMatchesFilter(f, {
        status: findingStatus,
        severity: findingSeverity,
      }),
    );
  }, [findings, findingStatus, findingSeverity]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ra = SEVERITY_RANK[a.severity as Severity] ?? 0;
      const rb = SEVERITY_RANK[b.severity as Severity] ?? 0;
      if (ra !== rb) return rb - ra;
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });
  }, [filtered]);

  const queueUrl = `/projects/${projectId}/findings?persona=${personaId}`;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <Kicker as="h2">Findings</Kicker>
          <span className="text-sm text-muted-foreground">
            What {firstName}'s runs revealed
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {findings.length > 0 && (
            <>
              <FindingsFilter
                status={findingStatus}
                severity={findingSeverity}
                onStatusChange={setFindingStatus}
                onSeverityChange={setFindingSeverity}
              />
              <Link
                to={queueUrl}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline whitespace-nowrap"
              >
                Open in queue
                <ArrowRight className="size-3" aria-hidden="true" />
              </Link>
            </>
          )}
        </div>
      </div>

      {findings.length === 0 ? (
        <Tonal padding="md">
          <p className="text-sm text-muted-foreground">
            No findings from {firstName} yet. Run a walkthrough to see what
            helps and hinders this persona.
          </p>
        </Tonal>
      ) : sorted.length === 0 ? (
        <FilteredEmptyState
          title="No findings match these filters"
          description={`None of ${firstName}'s ${findings.length} recorded findings match your current status or severity filter.`}
          onReset={() => {
            setFindingStatus("all");
            setFindingSeverity("all");
          }}
        />
      ) : (
        <div className="space-y-2">
          <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border px-4 py-1">
            {sorted.map((finding) => (
              <div key={finding.id} className="py-3 first:pt-2 last:pb-2">
                <Link
                  to={`/projects/${projectId}/findings/${finding.id}`}
                  className="group block transition-colors"
                >
                  <FindingRow
                    finding={{
                      id: finding.id,
                      title: finding.title ?? null,
                      description: finding.description,
                      type: finding.type as FindingType,
                      severity: finding.severity as Severity | null,
                    }}
                  />
                </Link>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
            <span>
              Showing {sorted.length} of {findings.length}{" "}
              {findings.length === 1 ? "finding" : "findings"}
            </span>
            <Link
              to={queueUrl}
              className="font-medium text-muted-foreground hover:text-foreground hover:underline transition-colors"
            >
              View all in triage queue →
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
